/**
 * The envelope page's shared window for the Register, the P&L and the workbook export (TMMAPP-0114
 * A3, Operator 2026-10-07): the same calendar presets as the virtual account P&L and the downloads
 * (`downloadRange.ts`) — Year to date (the default) · Last 12 months · This month · Last month · Last
 * year — plus Custom. The URL carries `from` / `to` as civil dates (`YYYY-MM-DD`); both absent is
 * Year to date, so the default leaves the URL clean. The Register and the export send the dates; the
 * P&L sends their months. The retired `default` preset (the envelope's own period, MM-D-40) is gone:
 * the budget narrative and progress still read the period tmm-api resolves, whatever the window.
 */
import {
  presetRange,
  RANGE_PRESETS,
  type RangePreset,
} from '@/features/virtualAccount/downloadRange';
import type { WindowDates } from './window';

export interface EnvelopeRange {
  /** Civil dates; both empty = Year to date. */
  from: string;
  to: string;
}

const CIVIL = /^\d{4}-\d{2}-\d{2}$/;

export const isCivilDate = (value: string): boolean => CIVIL.test(value);

/** The preset a range is, or `null` (a custom range). No dates = the default, Year to date. */
export function rangePresetOf(range: EnvelopeRange, today: string): RangePreset | null {
  if (range.from === '' && range.to === '') return 'ytd';
  return (
    RANGE_PRESETS.find((preset) => {
      const [from, to] = presetRange(preset, today);
      return from === range.from && to === range.to;
    }) ?? null
  );
}

/** A custom range is bad when both dates are set and the start is after the end. */
export function isBadDates(from: string, to: string): boolean {
  return isCivilDate(from) && isCivilDate(to) && from > to;
}

/**
 * The dates a request carries: Year to date's own when the URL has none, the range's dates
 * otherwise, or `null` for a bad custom range (nothing is requested until it is fixed). A half-set
 * custom range sends the one date it has.
 */
export function rangeDates(range: EnvelopeRange, today: string): WindowDates | null {
  if (range.from === '' && range.to === '') {
    const [from, to] = presetRange('ytd', today);
    return { from, to };
  }
  if (isBadDates(range.from, range.to)) return null;
  const dates: WindowDates = {};
  if (isCivilDate(range.from)) dates.from = range.from;
  if (isCivilDate(range.to)) dates.to = range.to;
  return dates;
}
